import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Grid, Paper, Stack, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { DEFAULT_SETTINGS, useSettings } from '../Data/settings';
import { CLIENT_NAME } from '../config/clientInfo';
import { Navigate } from 'react-router-dom';
import { getSession } from '../Data/auth';

export default function Settings() {
  return getSession()?.role === 1 ? <SettingsForm /> : <Navigate to="/" replace />;
}

function SettingsForm() {
  const { settings, loading, error, reloadSettings, saveSettings } = useSettings();
  const [draft, setDraft] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  useEffect(() => { setDraft(settings); }, [settings]);

  const validColor = /^#[0-9a-f]{6}$/i.test(draft.themeColor);
  const dirty = Object.keys(DEFAULT_SETTINGS).some(key => draft[key] !== settings[key]);
  const change = key => event => {
    setDraft(current => ({ ...current, [key]: event.target.value }));
    setSaveError('');
  };
  const submit = async event => {
    event.preventDefault();
    if (getSession()?.role !== 1) {
      setSaveError('Only Admin can save settings. Please sign in as Admin.');
      return;
    }
    if (saving || loading || error || !validColor || !draft.address.trim() || !draft.telephone.trim()) return;
    setSaving(true);
    setSaveError('');
    try {
      await saveSettings(draft);
      toast.success('Settings saved');
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Unable to save settings. Your changes have been kept; please try again.');
    } finally {
      setSaving(false);
    }
  };

  return <Box component="main" sx={{ pt: 11, pb: 4, px: { xs: 2, md: 4 }, maxWidth: 1050, mx: 'auto' }}>
    <Typography variant="h4" component="h1" gutterBottom>Settings</Typography>
    <Typography color="text.secondary" sx={{ mb: 3 }}>Manage receipt contact details and the application theme.</Typography>
    {loading ? <CircularProgress aria-label="Loading settings" /> : error ?
      <Alert severity="error" action={<Button color="inherit" onClick={reloadSettings}>Retry</Button>}>{error}</Alert> :
      <Box component="form" onSubmit={submit}>
        {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Stack spacing={3}>
                <Typography variant="h6">Receipt details</Typography>
                <TextField label="Billing address" multiline minRows={3} required fullWidth
                  value={draft.address} onChange={change('address')} disabled={saving}
                  inputProps={{ maxLength: 1000 }} helperText="Use a new line for each address line. Applies to billing and bill history reprints." />
                <TextField label="Telephone number" type="tel" required fullWidth value={draft.telephone}
                  onChange={change('telephone')} disabled={saving} inputProps={{ maxLength: 50 }} />
                <Typography variant="h6">Theme color</Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <input aria-label="Choose theme color" type="color" value={validColor ? draft.themeColor : settings.themeColor}
                    onChange={change('themeColor')} disabled={saving} style={{ width: 56, height: 48, cursor: 'pointer' }} />
                  <TextField label="Theme color (hex)" value={draft.themeColor} onChange={change('themeColor')}
                    disabled={saving} error={!validColor} helperText={validColor ? 'Applied when you save.' : 'Enter a color such as #9E1713.'} inputProps={{ maxLength: 7 }} />
                </Stack>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper variant="outlined" sx={{ p: 3, borderTop: '5px solid', borderTopColor: validColor ? draft.themeColor : settings.themeColor }}>
              <Typography variant="overline" color="text.secondary">Receipt header preview</Typography>
              <Box sx={{ textAlign: 'center', py: 3, fontFamily: 'monospace', color: '#000' }}>
                <Typography fontWeight="bold" gutterBottom>{CLIENT_NAME}</Typography>
                <Box sx={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>{draft.address}</Box>
                <Box sx={{ mt: 1, overflowWrap: 'anywhere' }}>Tel. {draft.telephone}</Box>
              </Box>
              <Typography variant="body2" color="text.secondary">Saved details are used for new receipts and history previews and reprints.</Typography>
            </Paper>
          </Grid>
        </Grid>
        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button type="submit" variant="contained" disabled={saving || !dirty || !validColor || !draft.address.trim() || !draft.telephone.trim()}>
            {saving ? 'Saving…' : 'Save settings'}
          </Button>
          <Button disabled={saving || !dirty} onClick={() => { setDraft(settings); setSaveError(''); }}>Discard changes</Button>
        </Stack>
      </Box>}
  </Box>;
}
